import { selectCurrency } from '@/redux/config/configSlice';
import { ItemType } from '@/redux/data/dataTypes';
import { useAppSelector } from '@/redux/hooks';
import { CloseOutlined } from '@ant-design/icons';
import { Button, Input, Modal } from 'antd';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface ItemCalculatorProps {
  item: ItemType;
  onClose: () => void;
  onSubmit: (item: ItemType) => void;
}

const ItemCalculator: FC<ItemCalculatorProps> = ({ item, onClose, onSubmit }) => {
  const { t } = useTranslation();
  const [amount, setAmount] = useState('');
  const currency = useAppSelector(selectCurrency);

  const handleSubmit = () => {
    if (item.sellingType === 'QUANTITY') {
      onSubmit({
        ...item,
        price: Number(amount),
      });
    } else {
      onSubmit({
        ...item,
        quantity: Number(amount),
      });
    }
  };

  const handleChangeAmount = (value: string) => {
    if (item.sellingType === 'QUANTITY') {
      const decimal = Number(value.replace(/\D/g, '')) / 100;
      const { format: formatCurrency } = Intl.NumberFormat('en-US', {
        currency: 'USD',
        style: 'currency',
      });
      return setAmount(formatCurrency(decimal || 0).replace('$', ''));
    }
    setAmount(value);
  };

  return (
    <Modal
      open
      onCancel={onClose}
      title={
        <div className="flex items-center gap-2.5">
          <Button type="text" icon={<CloseOutlined className="text-white" />} onClick={onClose} />
          <span className="text-white font-medium max-w-[200px] truncate">{item.name}</span>
        </div>
      }
      footer={null}
      className="[&_.ant-modal-header]:bg-primary [&_.ant-modal-title]:text-white"
    >
      <div className="flex flex-col gap-2.5">
        <span className="text-[15px] font-medium">
          {item.sellingType === 'QUANTITY' ? t('common:price') : t('common:weight')}
        </span>
        <div className="flex items-center">
          {item.sellingType === 'QUANTITY' && (
            <span className="text-gray-500 text-xl mr-2">{currency}</span>
          )}
          <Input
            autoFocus
            value={amount}
            placeholder={item.sellingType === 'QUANTITY' ? '0.00' : '0'}
            className="text-xl border-0"
            onChange={(e) => handleChangeAmount(e.target.value)}
          />
        </div>
        <Button
          type="primary"
          className="mt-5"
          disabled={!(parseFloat(amount) > 0)}
          onClick={handleSubmit}
        >
          {t('common:submit')}
        </Button>
      </div>
    </Modal>
  );
};

export default ItemCalculator;
